import { Component, OnDestroy, OnInit, ChangeDetectionStrategy } from '@angular/core';
import { ConfirmationModalConfig } from '../../../shared/components/confirmation-modal/confirmation-modal.component';
import { GtwSnackbarService } from '../../../shared/_services/gtw-snackbar.service';
import { CfcGroupElectService } from './cfc-group-elect.service';
import { GtwElementsBaseUrlsModel } from '../../../shared/_models/gtw-elements-base-urls.model';
import * as _ from 'lodash-es';
import { Subscription } from 'rxjs';
import { GlobalService } from '../../../global/services/global.service';
import { WorkspaceService } from '../../../workspace/workspace.service';
import { AppService } from '../../../app.service';
import {
  extractObject,
  generalConfigFromGlobal,
  gridPayloadFromActiveScreen,
  hydratedGlobalParams,
  normalizeFilterParams,
  rowPayloadFromActiveScreen
} from '../../../shared/workspace-element-context';

@Component({
    selector: 'gtw-cfc-group-elect',
    templateUrl: './cfc-group-elect.component.html',
    styleUrls: ['./cfc-group-elect.component.scss'],
    standalone: false,
  changeDetection: ChangeDetectionStrategy.Eager,
})
export class CfcGroupElectComponent implements OnInit, OnDestroy {

  private static readonly bridgeHostSelector = 'gtw-cfc-group-elect-ce';
  private static readonly actionEnable = 'setCFCGroupEnable';

  private subscriptions = new Subscription();
  private baseGlobalParams: any = {};
  /** Grid action from CeHostDialog bridge: activeScreen.data.modal_name */
  private gridAction = '';

  filterParams: any = {};
  baseURLs: GtwElementsBaseUrlsModel = {'admin':'','api':'','custom':'','efile':'','pdfEngine':'','bulkPdfEngine':''};
  rowData: any = {};

  modalConfig: ConfirmationModalConfig = {
    title: 'Confirm',
    message: 'Are you sure you want to update the CFC Group status for selected entities?',
    iconClass: 'fa fa-sign-out',
    iconColor: 'txt-color-orange-dark',
    confirmButtonText: 'Yes',
    cancelButtonText: 'No',
    confirmButtonClass: 'btn-danger',
    cancelButtonClass: 'btn-default',
    showCancelButton: true,
    showConfirmButton: true,
    animationClass: 'animated fadeIn fast',
    loading: false,
    loadingText: 'Processing...'
  };

  globalService: any = {};
  USER_SETTINGS: any = {};
  SERVER_CONFIG: any = {};
  GENERAL_CONFIG: any = {};

  showModal: boolean = false;
  cfcGroupDtls: any[] = [];
  logged_in_user: string = '';
  tax_year: string = '';
  scenario: string = '';
  jcd_key: string = '';

  constructor(
    private gtwSnackbarService: GtwSnackbarService,
    private cfcGroupElectService: CfcGroupElectService,
    private gtwGlobal: GlobalService,
    private gtwWorkspace: WorkspaceService,
    private gtwApp: AppService
  ) { }

  ngOnInit(): void {
    this.applyWorkspaceContext();
    this.tryInit();
    this.subscriptions.add(this.gtwWorkspace.setTabActive.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
    this.subscriptions.add(this.gtwGlobal.globalParamsChanged$.subscribe(() => {
      this.applyWorkspaceContext();
      this.tryInit();
    }));
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  private applyWorkspaceContext(): void {
    const activeScreen = this.gtwWorkspace.workspaceFactory?.activeScreen || {};
    this.baseURLs = generalConfigFromGlobal(this.gtwGlobal);
    this.GENERAL_CONFIG = this.baseURLs;
    this.baseGlobalParams = hydratedGlobalParams(this.gtwGlobal, this.gtwWorkspace) || {};
    let fp = normalizeFilterParams(activeScreen) || {};
    if (!Object.keys(fp).length) {
      fp = extractObject(this.baseGlobalParams);
    }
    this.filterParams = fp;

    const gridPayload = gridPayloadFromActiveScreen(activeScreen);
    const rawRow = rowPayloadFromActiveScreen(activeScreen);
    if (Array.isArray(gridPayload) && gridPayload.length > 0) {
      const o: any = {};
      gridPayload.forEach((r: any, i: number) => {
        o[i] = r?.data !== undefined ? r : { data: r };
      });
      this.rowData = o;
    } else if (Array.isArray(rawRow)) {
      const o: any = {};
      rawRow.forEach((r: any, i: number) => {
        o[i] = r?.data !== undefined ? r : { data: r };
      });
      this.rowData = o;
    } else if (rawRow && typeof rawRow === 'object' && Object.keys(rawRow).length > 0) {
      const firstKey = Object.keys(rawRow)[0];
      const firstVal = rawRow[firstKey];
      const looksLikeBatchMap =
        firstVal && typeof firstVal === 'object' && firstVal.data && typeof firstVal.data === 'object';
      this.rowData = looksLikeBatchMap ? rawRow : { 0: { data: rawRow } };
    } else {
      this.rowData = {};
    }

    if (this.gtwApp?.USER_SETTINGS?.user?.sso_id) {
      this.USER_SETTINGS = this.gtwApp.USER_SETTINGS;
      this.logged_in_user = String(this.gtwApp.USER_SETTINGS.user.sso_id);
    }
    if (this.gtwApp?.SERVER_CONFIG) {
      this.SERVER_CONFIG = this.gtwApp.SERVER_CONFIG;
    }

    const gp = this.baseGlobalParams || {};
    this.globalService = {
      ...gp,
      modalScreen: gp?.modalScreen,
      globalParams: gp
    };

    this.resolveGridAction(activeScreen);
  }

  /**
   * Enable vs disable is determined by the grid action name bridged into
   * activeScreen by CeHostDialog (modal_name), not by CE HTML attributes.
   */
  private resolveGridAction(activeScreen: any): void {
    const asData = activeScreen?.data || {};
    this.gridAction = String(
      asData.modal_name
      || asData.modalName
      || asData.colData?.call_back
      || this.baseGlobalParams?.modalScreen?.modal_name
      || ''
    ).trim();
  }

  private colValueForSave(): number {
    return this.gridAction === CfcGroupElectComponent.actionEnable ? 1 : 0;
  }

  init(): void {
    if (this.baseURLs && Object.keys(this.baseURLs).length > 0 &&
        this.filterParams && Object.keys(this.filterParams).length > 0 &&
        this.GENERAL_CONFIG && Object.keys(this.GENERAL_CONFIG).length > 0) {
      this.initializeData();
    }
  }

  private tryInit(): void {
    this.init();
  }

  initializeData(): void {
    if (this.USER_SETTINGS?.user?.sso_id) {
      this.logged_in_user = this.USER_SETTINGS.user.sso_id;
    }

    this.tax_year = this.filterParams?.tax_year || this.rowData?.TAX_YEAR || this.globalService?.tax_year || '';
    this.scenario = this.filterParams?.scenario || this.rowData?.SCENARIO || this.globalService?.scenario || '';
    this.jcd_key = this.filterParams?.jcd_key || this.rowData?.JCD_KEY || this.globalService?.jcd_key || '';

    if (this.rowData) {
      this.processGridData();
    }
  }

  processGridData(): void {
    this.cfcGroupDtls = [];
    if (this.rowData == null) {
      return;
    }

    const gridDataMultipleSelect: any = _.clone(this.rowData);
    if (gridDataMultipleSelect == undefined) {
      return;
    }

    const keys = Object.keys(gridDataMultipleSelect);
    const firstVal = keys.length ? gridDataMultipleSelect[keys[0]] : null;
    const looksLikeRowCollection =
      firstVal && typeof firstVal === 'object'
      && ((firstVal.data && typeof firstVal.data === 'object')
          || ('LEID' in firstVal) || ('COMBINATION_KEY' in firstVal)
          || ('ME_CODE' in firstVal) || ('CDR_NO' in firstVal));

    const rows: any[] = looksLikeRowCollection
      ? keys.map(k => gridDataMultipleSelect[k])
      : [{ data: gridDataMultipleSelect }];

    const colValue = this.colValueForSave();
    rows.forEach((entry: any) => {
      const dataItem = entry?.data ?? entry;
      if (!dataItem || typeof dataItem !== 'object') {
        return;
      }
      this.cfcGroupDtls.push({
        combination_key: dataItem.COMBINATION_KEY,
        acct_key: dataItem.CFC_GROUP_ACCT_KEY,
        amount: dataItem.CFC_GROUP,
        COL_VALUE: colValue,
        leid: dataItem.LEID,
        me_code: dataItem.ME_CODE,
        cdr_number: dataItem.CDR_NO,
      });
    });
  }

  onConfirm(): void {
    this.confirmRefresh();
  }

  /**
   * Close the dialog. `detail` is forwarded to `CeHostDialog.listenForEvents`:
   *   - 'cancel'  → dialog closes with `{ action: 'close' }` (no grid refresh)
   *   - anything else (e.g. 'refresh') → dialog closes with
   *     `{ action: 'refresh', detail }` so `grid.directive` reloads the grid.
   */
  cancel(detail: string = 'cancel'): void {
    this.showModal = false;

    const host = document.querySelector(CfcGroupElectComponent.bridgeHostSelector);
    host?.dispatchEvent(
      new CustomEvent('request-grid-update', { detail, bubbles: true })
    );
  }

  onCancel(): void {
    this.cancel('cancel');
  }

  confirmRefresh(): void {
    const returnClobSettingsObj: any = {};

    returnClobSettingsObj['sso_id'] = this.logged_in_user;
    returnClobSettingsObj['tax_year'] = this.tax_year;
    returnClobSettingsObj['scenario'] = this.scenario;
    returnClobSettingsObj['jcd_key'] = this.jcd_key;
    returnClobSettingsObj['scenarioCode'] = 'RAF';
    returnClobSettingsObj['is_issue_key_enabled'] = 'N';

    if (!this.tax_year || !this.scenario || !this.jcd_key) {
      this.gtwSnackbarService.openSnackBar('Required configuration parameters (tax_year, scenario, jcd_key) are missing! Please Contact System Administrator', 'gtw-snackbar--red');
      return;
    }

    if (this.cfcGroupDtls != undefined && this.cfcGroupDtls.length > 0) {
      this.sendDetails(
        returnClobSettingsObj,
        this.cfcGroupDtls,
        'Status update for CFC group selected Entity is success'
      );
    }
  }

  sendDetails(returnClobSettingsObj: any, ecSaveDtls: any[], message: string): void {
    this.modalConfig = { ...this.modalConfig, loading: true };

    const apiBase = this.GENERAL_CONFIG.base_url || this.GENERAL_CONFIG.api || '';
    const sub = this.cfcGroupElectService.setCFCGroup(
      apiBase,
      returnClobSettingsObj,
      ecSaveDtls,
      this.globalService,
      this.filterParams,
      this.rowData
    ).subscribe(
      (result: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };

        if (result.callSuccess === '1' && result.errorMessage === 'null') {
          this.gtwSnackbarService.openSnackBar(message, 'gtw-snackbar--green');
          this.cancel('refresh');
        } else {
          this.gtwSnackbarService.openSnackBar(result.errorMessage, 'gtw-snackbar--red');
        }
      },
      (error: any) => {
        this.modalConfig = { ...this.modalConfig, loading: false };

        console.error('CFC Group update error:', error);
        this.gtwSnackbarService.openSnackBar('Error occurred while updating CFC Group status. Please try again.', 'gtw-snackbar--red');
      }
    );
    this.subscriptions.add(sub);
  }
}
